// Das Namensschild über dem Kopf eines Mitspielers (task-016, docs/architektur-paket-2.md, Abschnitt
// „Client“). Nicht zu verwechseln mit den Schildern an den Toren der Parzellen (render/schilder.ts).
//
// Hier ist ein Sprite richtig, anders als am Tor (ADR 0025): Das Schild schwebt frei über dem Kopf, und
// es soll aus jeder Richtung lesbar sein, aus der man den Mitspieler sieht. Gemalt wird auf ein Canvas;
// die Tafel hängt als Kind an der Figur und wandert mit ihr. Hinter einer Wand verschwindet es wie die
// Figur selbst.
import { CanvasTexture, LinearFilter, SRGBColorSpace, Sprite, SpriteMaterial } from 'three';
import { PLAYER } from '@bauernhof/shared';
import { schildText } from './schilder.js';

/** Lage und Größe des Namensschilds in Blöcken; `hoehe` ist die Mitte über dem Fußpunkt. */
export const NAMENSSCHILD = {
  tafelHoehe: 0.3,
  breite: 1.6,
  hoehe: PLAYER.height + 0.2 + 0.3 / 2,
} as const;

/** Auflösung der Malfläche; das Seitenverhältnis ist das der Tafel. */
const MALFLAECHE = { breite: 256, hoehe: 48 } as const;
const FARBEN = { grund: 'rgba(40, 28, 16, 0.8)', rand: '#b08a56', schrift: '#f4ecd8' } as const;

/** Der Name, wie er auf dem Schild steht: ohne Leerraum am Rand, gekürzt wie auf den Hofschildern. */
export function namensText(name: string): string {
  return schildText(name);
}

export interface Namensschild {
  /** Das Sprite; der Aufrufer hängt es an die Figur. */
  object: Sprite;
  setName(name: string): void;
  /** Gibt Textur und Material frei; aus der Szene nimmt es die Figur mit. */
  dispose(): void;
}

export function createNamensschild(name: string): Namensschild {
  const canvas = document.createElement('canvas');
  canvas.width = MALFLAECHE.breite;
  canvas.height = MALFLAECHE.hoehe;
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('Das Namensschild braucht einen 2d-Kontext; der Browser gab keinen her.');

  const texture = new CanvasTexture(canvas);
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;
  texture.colorSpace = SRGBColorSpace;
  // Kein Tiefenschreiben: Die durchsichtigen Ränder sollen nichts dahinter verdecken.
  const material = new SpriteMaterial({ map: texture, transparent: true, depthWrite: false });
  const object = new Sprite(material);
  object.name = 'namensschild';
  object.scale.set(NAMENSSCHILD.breite, NAMENSSCHILD.tafelHoehe, 1);
  object.position.set(0, NAMENSSCHILD.hoehe, 0);

  let gemalt: string | null = null;
  const setName = (neu: string): void => {
    const text = namensText(neu);
    if (text === gemalt) return;
    gemalt = text;
    malNamensschild(ctx, text);
    texture.needsUpdate = true;
  };
  setName(name);

  return {
    object,
    setName,
    dispose() {
      texture.dispose();
      material.dispose();
    },
  };
}

/** Malt eine dunkle, abgerundete Tafel so breit wie der Name, mit heller Schrift. */
function malNamensschild(ctx: CanvasRenderingContext2D, text: string): void {
  const { breite: b, hoehe: h } = MALFLAECHE;
  ctx.clearRect(0, 0, b, h);
  if (text === '') return;
  ctx.font = 'bold 30px system-ui, sans-serif';
  const tafel = Math.min(b - 4, ctx.measureText(text).width + 28);
  ctx.beginPath();
  ctx.roundRect((b - tafel) / 2, 3, tafel, h - 6, 12);
  ctx.fillStyle = FARBEN.grund;
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = FARBEN.rand;
  ctx.stroke();
  ctx.fillStyle = FARBEN.schrift;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, b / 2, h / 2 + 1, b - 20);
}
